import { rootComponentsStore } from "../../../hookstate/rootComponentsStore";
import { SLIDE_IN_MENUS } from "../../../screens/root/constants";

const PENDING_STRIPE_RETURN_SLIDE_IN_MENU_KEY =
  "pendingStripeReturnSlideInMenu";

type PendingStripeReturnSlideInMenuState = {
  openMenu: SLIDE_IN_MENUS;
  menuConfig: Record<string, unknown>;
};

function isSlideInMenuValue(value: unknown): value is SLIDE_IN_MENUS {
  return (
    typeof value === "string" &&
    Object.values(SLIDE_IN_MENUS).includes(value as SLIDE_IN_MENUS)
  );
}

function createMenuInstanceId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `menu_${Date.now()}_${Math.random().toString(16).slice(2, 10)}`;
}

export function persistStripeReturnSlideInMenuState() {
  if (typeof window === "undefined") {
    return;
  }

  const openMenu = rootComponentsStore.openSlideInMenu.get({ noproxy: true });
  const currentMenuConfig = rootComponentsStore.slideInMenuConfig.get({
    noproxy: true,
  });
  const history = rootComponentsStore.slideInMenuHistory.get({ noproxy: true });

  let payload: PendingStripeReturnSlideInMenuState | undefined;

  if (openMenu) {
    payload = {
      openMenu,
      menuConfig: (currentMenuConfig ?? {}) as Record<string, unknown>,
    };
  } else if (
    currentMenuConfig &&
    typeof currentMenuConfig.comicSlug === "string" &&
    currentMenuConfig.comicSlug.length > 0
  ) {
    // If the menu temporarily closed while transitioning to payment funnel,
    // keep enough context to reopen the series details shell after return.
    payload = {
      openMenu: SLIDE_IN_MENUS.comicDetails,
      menuConfig: {
        comicSlug: currentMenuConfig.comicSlug,
      },
    };
  } else if (history.length > 0 && isSlideInMenuValue(history[0].type)) {
    payload = {
      openMenu: history[0].type,
      menuConfig: (history[0].config ?? {}) as Record<string, unknown>,
    };
  }

  if (!payload) {
    window.sessionStorage.removeItem(PENDING_STRIPE_RETURN_SLIDE_IN_MENU_KEY);
    return;
  }

  window.sessionStorage.setItem(
    PENDING_STRIPE_RETURN_SLIDE_IN_MENU_KEY,
    JSON.stringify(payload),
  );
}

export function restoreStripeReturnSlideInMenuState() {
  if (typeof window === "undefined") {
    return;
  }

  const serializedValue = window.sessionStorage.getItem(
    PENDING_STRIPE_RETURN_SLIDE_IN_MENU_KEY,
  );
  if (!serializedValue) {
    return;
  }

  window.sessionStorage.removeItem(PENDING_STRIPE_RETURN_SLIDE_IN_MENU_KEY);

  try {
    const parsed = JSON.parse(serializedValue) as PendingStripeReturnSlideInMenuState;
    if (!isSlideInMenuValue(parsed.openMenu)) {
      return;
    }

    const menuConfig =
      typeof parsed.menuConfig === "object" && parsed.menuConfig !== null
        ? parsed.menuConfig
        : {};

    rootComponentsStore.slideInMenuConfig.set({
      ...menuConfig,
      menuInstanceId: createMenuInstanceId(),
    });
    rootComponentsStore.openSlideInMenu.set(parsed.openMenu);
  } catch {
    // Ignore malformed persisted state.
  }
}
